import React from 'react'
import { Space, Tag, Button, Popover, message } from 'antd'
import { CopyToClipboard } from 'react-copy-to-clipboard'

let nf = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

// Exchange rates need a couple of decimals (e.g. 1 USD = GH₵ 13.50).
let rf = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4
})

const getWhatsAppText = record => {
  const chassisString = record.chassisNumber
    ? `\n*Chassis Number:* ${record.chassisNumber.toUpperCase()}`
    : ''
  const engineString = record.engineSize
    ? `\n*Engine:* ${Number(record.engineSize).toFixed(1)}L`
    : ''
  const fuelString = record.fuelType
    ? `\n*Fuel Type:* ${record.fuelType.name}`
    : ''
  const driveTrainString = record.driveTrain
    ? `\n*Drive Train:* ${record.driveTrain.name}`
    : ''
  const originString = record.origin
    ? `\n*Origin:* ${record.origin.country}`
    : ''

  const ds = record.dutySummary
  // const hasRateRange = ds && ds.usedRateLowest && ds.usedRateHighest

  // Main exchange rate, shown for the MSRP currency that drove the calculation.
  let exchangeRateText = ''
  if (ds && ds.usedRate) {
    const rateCurrency = ds.usedRateCurrency || record.msrpCurrency || 'USD'
    exchangeRateText = `\n\n*Exchange Rate Used:* 1 ${rateCurrency} = GH₵ ${rf.format(
      ds.usedRate
    )}`
  }

  // Main duty at the main rate, with the possible range as a note beneath it.
  let dutyText = ''
  if (ds && (ds.total || ds.dutyLowerEstimatedValue)) {
    const mainDuty = ds.total || ds.dutyLowerEstimatedValue
    dutyText = `\n\n*Estimated Duty:* GH₵ ${nf.format(mainDuty)}`
    if (ds.dutyLowerEstimatedValue && ds.dutyHigherEstimatedValue) {
      dutyText += `\n(may range between GH₵ ${nf.format(
        ds.dutyLowerEstimatedValue
      )} [@${rf.format(
        ds.usedRateLowest
      )}] and GH₵ ${nf.format(
        ds.dutyHigherEstimatedValue
      )} [@${rf.format(ds.usedRateHighest)}] depending on the exchange rate at the time of clearing.)`
    }
    // if (hasRateRange) {
    //   dutyText += `\n*Exchange rate may vary between:* GH₵ ${rf.format(
    //     ds.usedRateLowest
    //   )} - GH₵ ${rf.format(ds.usedRateHighest)}`
    // }
  }

  let portText = ''
  if (record.dutySummary && record.dutySummary.portChargesLowerValue) {
    portText = `\n\n*Estimated Clearing Charges:* GH₵ ${nf.format(
      record.dutySummary.portChargesLowerValue
    )} - GH₵ ${nf.format(
      record.dutySummary.portChargesHigherValue
    )}\n(This will depend on the clearing agent handling it. Some charge less fees than others.)`
  }

  const carName = `*Car:* ${record.carYear || ''} ${record.make?.name ||
    ''} ${record.model?.name || ''} ${record.trim || ''}`
    .trim()
    .toUpperCase()

  return `*CUSTOM DUTIES ESTIMATE*\n\n${carName}${originString}${engineString}${fuelString}${driveTrainString}${chassisString}${exchangeRateText}${dutyText}${portText}`
}

export const columns = (
  setShowUpdateModal,
  setCurrentCarDetail,
  setShowDeleteModal,
  handleRegenerateDocument,
  regeneratingId
) => {
  return [
    {
      title: 'Car Make',
      dataIndex: 'make',
      key: 'make',
      width: 250,
      render: (text, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="mb-2">
            <b>
              {String(
                `${record.carYear} ${record.make?.name || ''} ${record.model
                  ?.name || ''} ${record.trim || ''}`
              ).toUpperCase()}
            </b>
            <br />
            {record.chassisNumber && (
              <span>
                <b>Chassis:</b> {record.chassisNumber.toUpperCase()}
                <br />
              </span>
            )}
            <b>Origin:</b> {record.origin?.country} ({record.condition?.name})
            <br />
            <b>Specs:</b> {record.engineSize}L, {record.fuelType?.name},{' '}
            {record.driveTrain?.name}
            <br />
            <b>MSRP:</b>{' '}
            {record.msrpInUsd
              ? `${nf.format(record.msrpInUsd)} ${record.msrpCurrency || 'USD'}`
              : 'N/A'}
          </div>
          <div>
            <Tag color={record.isSpecialPurpose ? 'purple' : 'blue'}>
              {record.isSpecialPurpose ? 'Special Purpose' : 'Standard'}
            </Tag>
            <Tag
              color={
                record.status === 'COMPLETED'
                  ? 'green'
                  : record.status === 'PROCESSING'
                  ? 'orange'
                  : 'default'
              }
            >
              {record.status}
            </Tag>
            {record.customer && (
              <Popover
                content={
                  <div>
                    <p>
                      <b>Name:</b> {record.customer.momoName || 'N/A'}
                    </p>
                    <p>
                      <b>Phone:</b> {record.customer.phone || 'N/A'}
                    </p>
                    <p>
                      <b>Momo Phone:</b> {record.customer.momoPhone || 'N/A'}
                    </p>
                    <p>
                      <b>Momo Network:</b>{' '}
                      {record.customer.momoNetwork || 'N/A'}
                    </p>
                    <p>
                      <b>Email:</b> {record.customer.email || 'N/A'}
                    </p>
                  </div>
                }
                title="Customer Details"
                trigger="click"
              >
                <Tag color="cyan" style={{ cursor: 'pointer' }}>
                  Customer Details
                </Tag>
              </Popover>
            )}
          </div>
        </div>
      )
    },
    {
      title: 'Duty Summary',
      dataIndex: 'dutySummary',
      key: 'dutySummary',
      width: 300,
      render: (text, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {record.dutySummary ? (
            <>
              <div>
                <b>Estimated Duty:</b> GH₵{' '}
                {nf.format(
                  record.dutySummary.total ||
                    record.dutySummary.dutyLowerEstimatedValue
                )}
              </div>
              {record.dutySummary.dutyLowerEstimatedValue &&
                record.dutySummary.dutyHigherEstimatedValue && (
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    Range: GH₵{' '}
                    {nf.format(record.dutySummary.dutyLowerEstimatedValue)} -{' '}
                    {nf.format(record.dutySummary.dutyHigherEstimatedValue)}{' '}
                    (depends on the exchange rate)
                  </div>
                )}
              <div style={{ fontSize: '12px', color: '#888' }}>
                Rate: 1{' '}
                {record.dutySummary.usedRateCurrency ||
                  record.msrpCurrency ||
                  'USD'}{' '}
                = GH₵ {rf.format(record.dutySummary.usedRate)}
                {record.dutySummary.usedRateLowest &&
                  record.dutySummary.usedRateHighest &&
                  ` (GH₵ ${rf.format(
                    record.dutySummary.usedRateLowest
                  )} - ${rf.format(record.dutySummary.usedRateHighest)})`}
              </div>
              <div>
                <b>Port Charges:</b> GH₵{' '}
                {nf.format(record.dutySummary.portChargesLowerValue)} -{' '}
                {nf.format(record.dutySummary.portChargesHigherValue)}
              </div>
            </>
          ) : (
            'N/A'
          )}
        </div>
      )
    },
    {
      title: 'Date Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (text, record) => (
        <Space size="middle">
          {`${new Date(record.updatedAt).toDateString()} - ${new Date(
            record.updatedAt
          ).toLocaleTimeString()}`}
        </Space>
      )
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            type="primary"
            onClick={() => {
              setCurrentCarDetail(record)
              setShowUpdateModal(true)
            }}
            size="small"
          >
            Update
          </Button>

          <Popover
            content={
              <pre style={{ maxWidth: '400px', whiteSpace: 'pre-wrap' }}>
                {getWhatsAppText(record)}
              </pre>
            }
            title="WhatsApp Message Preview"
            trigger="hover"
          >
            <CopyToClipboard
              text={getWhatsAppText(record)}
              onCopy={() => {
                message.success('Copied to clipboard')
              }}
            >
              <Button
                type="default"
                size="small"
                style={{ borderColor: '#25D366', color: '#25D366' }}
              >
                Copy
              </Button>
            </CopyToClipboard>
          </Popover>

          {record.dutyDocumentUrl && (
            <>
              <Button
                size="small"
                onClick={() =>
                  window.open(record.dutyDocumentUrl, '_blank', 'noopener')
                }
              >
                View PDF
              </Button>
              <CopyToClipboard
                text={record.dutyDocumentUrl}
                onCopy={() => message.success('Document link copied')}
              >
                <Button size="small">Copy Link</Button>
              </CopyToClipboard>
            </>
          )}

          {/* Companion PNG image is disabled for now — we keep only the PDF document.
              To restore, un-comment this block (and the w-api image generation).
          {record.dutyDocumentImageUrl && (
            <>
              <Button
                size="small"
                onClick={() =>
                  window.open(record.dutyDocumentImageUrl, '_blank', 'noopener')
                }
              >
                View Image
              </Button>
              <CopyToClipboard
                text={record.dutyDocumentImageUrl}
                onCopy={() => message.success('Image link copied')}
              >
                <Button size="small">Copy Image Link</Button>
              </CopyToClipboard>
            </>
          )} */}

          <Button
            size="small"
            loading={regeneratingId === record.id}
            onClick={() => handleRegenerateDocument(record)}
          >
            {record.dutyDocumentUrl ? 'Update Document' : 'Generate Document'}
          </Button>

          <Button
            type="primary"
            danger
            onClick={() => {
              setCurrentCarDetail(record)
              setShowDeleteModal(true)
            }}
            size="small"
          >
            Delete
          </Button>
        </div>
      )
    }
  ]
}
